import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import debounce from "lodash.debounce";
import {
  Typography,
  Box,
  Button,
  FormControl,
  TextField,
  Autocomplete,
  InputAdornment,
  IconButton,
  Stack,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import CancelIcon from "@mui/icons-material/Cancel";
import {
  Book,
  Calendar,
  Check,
  Hash,
  MapPin,
  Type,
  X,
} from "react-feather";
import {
  applyStatuteFilters,
  statuteSearchResults,
} from "../../actions/statuteSearchActions";

const alphabets = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

const StatuteSearchSidebar = ({
  isSidebarVisible,
  toggleSidebar,
  selectedLetter,
  selectedYear,
  updateSelectedYear,
  updateSelectedLetter,
  resultsCount,
  currentPage,
  updateCurrentPage,
  searchMode,
  statuteSearchValue,
  selectedStatuteProvinceFilter,
  setSelectedStatuteProvinceFilter,
  selectedStatuteTypeFilter,
  setSelectedStatuteTypeFilter,
}) => {
  const dispatch = useDispatch();

  const isStatuteFilterAppliedState = useSelector(
    (state) => state.isStatuteFilterApplied
  );
  const { isStatuteSearchFilterApplied } = isStatuteFilterAppliedState;

  const [isFilterApplied, setIsFilterApplied] = useState(
    isStatuteSearchFilterApplied || false
  );
  const [yearError, setYearError] = useState("");

  // Tab colors for consistent theming
  const tabColors = {
    primary: "#00509e",
    hover: "#003366",
    secondary: "#6B7280",
  };

  // Debounced API call
  const debouncedApiCall = debounce((value) => {
    // Trigger API request only when year is valid
    dispatch(
      applyStatuteFilters(
        statuteSearchValue,
        {
          year: value,
          letter: selectedLetter,
          province: selectedStatuteProvinceFilter,
          type: selectedStatuteTypeFilter,
        },
        searchMode
      )
    );
  }, 1000);

  const handleYearChange = (event) => {
    const value = event.target.value;

    // Check if input is numeric
    if (/^\d*$/.test(value)) {
      updateSelectedYear(value);
      setYearError("");

      // Check if the input has 4 digits
      if (value.length === 4) {
        debouncedApiCall(value);
        setIsFilterApplied(true);
      } else if (value.length < 4 && value !== "") {
        setYearError("Year must be exactly 4 digits");
      }
    } else {
      setYearError("Please enter a valid numeric year");
    }

    if (!value && isFilterApplied) {
      dispatch(
        applyStatuteFilters("", {
          year: "",
          letter: selectedLetter,
          province: selectedStatuteProvinceFilter,
          type: selectedStatuteTypeFilter,
          searchMode,
        })
      );
    }
  };

  const handleLetterChange = (value) => {
    if (value === null) {
      updateSelectedLetter("");
    } else {
      updateSelectedLetter(value);
      dispatch({ type: "SET_IS_STATUTE_SEARCH_FILTER_APPLIED", payload: true });
      setIsFilterApplied(true);
      updateCurrentPage(1);
      dispatch(
        applyStatuteFilters(
          statuteSearchValue,
          {
            year: selectedYear,
            letter: value,
            province: selectedStatuteProvinceFilter,
            type: selectedStatuteTypeFilter,
          },
          searchMode,
          resultsCount,
          (currentPage - 1) * resultsCount
        )
      );
    }
  };

  const handleClearFilters = () => {
    updateSelectedLetter("");
    setSelectedStatuteProvinceFilter("");
    setSelectedStatuteTypeFilter("");
    updateSelectedYear("");
    setIsFilterApplied(false);
    updateCurrentPage(1);
    dispatch({ type: "SET_IS_STATUTE_SEARCH_FILTER_APPLIED", payload: false });
    dispatch(
      statuteSearchResults(
        statuteSearchValue,
        searchMode,
        resultsCount,
        (currentPage - 1) * resultsCount,
        { year: "", letter: "", province: "", type: "" }
      )
    );
  };

  const clearYear = () => {
    setYearError(""); // Optionally clear error on reset
    updateSelectedYear("");
    dispatch({ type: "SET_IS_STATUTE_SEARCH_FILTER_APPLIED", payload: true });
    setIsFilterApplied(true);
    dispatch(
      applyStatuteFilters(
        statuteSearchValue,
        {
          year: "",
          letter: selectedLetter,
          province: selectedStatuteProvinceFilter,
          type: selectedStatuteTypeFilter,
        },
        searchMode
      )
    );
  };

  const handleTypeChange = (value) => {
    if (value === null) {
      setSelectedStatuteTypeFilter("");
    } else {
      setSelectedStatuteTypeFilter(value);
      dispatch({ type: "SET_IS_STATUTE_SEARCH_FILTER_APPLIED", payload: true });
      setIsFilterApplied(true);
      updateCurrentPage(1);
      dispatch(
        applyStatuteFilters(
          statuteSearchValue,
          {
            year: selectedYear,
            letter: selectedLetter,
            province: selectedStatuteProvinceFilter,
            type: value,
          },
          searchMode,
          resultsCount,
          (currentPage - 1) * resultsCount
        )
      );
    }
  };

  const handleProvinceChange = (value) => {
    if (value === null) {
      setSelectedStatuteProvinceFilter("");
    } else {
      setSelectedStatuteProvinceFilter(value);
      dispatch({ type: "SET_IS_STATUTE_SEARCH_FILTER_APPLIED", payload: true });
      setIsFilterApplied(true);
      updateCurrentPage(1);
      dispatch(
        applyStatuteFilters(
          statuteSearchValue,
          {
            year: selectedYear,
            letter: selectedLetter,
            province: value,
            type: selectedStatuteTypeFilter,
          },
          searchMode,
          resultsCount,
          (currentPage - 1) * resultsCount
        )
      );
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
       backgroundColor: "#F8FAFC",
        flex: 1,
        width: { xs: "278px", lg: "20%" },
        height: "88vh",
        p: "12px 8px 0 12px",
        overflowY: "auto",
        "::-webkit-scrollbar": {
          width: "5px",
        },
        "::-webkit-scrollbar-track": {
          background: "#fff",
        },
        "::-webkit-scrollbar-thumb": {
          background: "#bbb",
          borderRadius: "6px",
        },
        fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
      }}
    >
      <Box
        sx={{
          height: "100%",
          display: "flex",
          boxSizing: "border-box",
          flexDirection: "column",
          bgcolor: "#fff",
          p: "8px",
          borderRadius: "8px",
          flexGrow: 1,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            padding: "16px 16px 12px 16px",
            borderBottom: "1px solid #F1F5F9",
            backgroundColor: "#FFFFFF",
            borderRadius: "8px 8px 0 0",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "16px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "40px",
                  height: "40px",
                  backgroundColor: `${tabColors.primary}15`,
                  borderRadius: "12px",
                  color: tabColors.primary,
                }}
              >
                <Book size={20} />
              </Box>
              <Typography
                sx={{
                  fontSize: "18px",
                  fontWeight: 600,
                  color: "#111827",
                  fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                }}
              >
                Statute Filters
              </Typography>
            </Box>
          </Box>

          {/* Clear Filters Button */}
          {((selectedLetter && isFilterApplied) ||
            (selectedYear && isFilterApplied) ||
            (selectedStatuteProvinceFilter && isFilterApplied) ||
            (selectedStatuteTypeFilter && isFilterApplied)) && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<X size={16} />}
              onClick={handleClearFilters}
              sx={{
                textTransform: "none",
                fontSize: "12px",
                fontWeight: 500,
                color: "#EF4444",
                borderColor: "#FECACA",
                backgroundColor: "#FEF2F2",
                borderRadius: "8px",
                padding: "6px 12px",
                height: "32px",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  backgroundColor: "#FEE2E2",
                  borderColor: "#FCA5A5",
                },
              }}
            >
              Clear All Filters
            </Button>
          )}
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, padding: "16px" }}>
          {/* Letter Filter */}
          <Typography
            sx={{
              fontSize: "0.75rem",
              fontWeight: 600,
              color: "#1F2937",
              mb: 0.5,
              ml: 0.5,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <Hash size={16} color="#0073A6" />
            Letter
          </Typography>
          <FormControl fullWidth variant="outlined" sx={{ mb: 1.5 }}>
            <Autocomplete
              size="small"
              value={selectedLetter || ""}
              onChange={(event, newValue) => {
                handleLetterChange(newValue);
              }}
              onInputChange={(e, newValue) => {
                if (!newValue && isFilterApplied) {
                  dispatch(
                    statuteSearchResults(
                      statuteSearchValue,
                      searchMode,
                      resultsCount,
                      (currentPage - 1) * resultsCount,
                      {
                        year: selectedYear,
                        letter: "",
                        province: selectedStatuteProvinceFilter,
                        type: selectedStatuteTypeFilter,
                      }
                    )
                  );
                }
              }}
              disableClearable={!selectedLetter}
              clearIcon={selectedLetter ? <ClearIcon fontSize="small" /> : null}
              options={alphabets}
              getOptionLabel={(option) => option || ""}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Select Letter"
                  sx={{
                    width: "95%",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        borderColor: "rgba(0, 61, 120, 0.2)",
                      },
                      "&.Mui-focused": {
                        boxShadow: "0 0 0 2px rgba(0, 61, 120, 0.1)",
                        borderColor: "#003D78",
                      },
                    },
                    "& .MuiInputBase-input": {
                      fontSize: "0.75rem",
                      padding: "8px 12px",
                    },
                    "& .MuiInputBase-input::placeholder": {
                      fontSize: "0.75rem",
                    },
                  }}
                />
              )}
              componentsProps={{
                paper: {
                  sx: {
                    mt: 0.5,
                    boxShadow:
                      "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                    borderRadius: "8px",
                    "& .MuiAutocomplete-listbox": {
                      fontSize: "0.75rem",
                      maxHeight: "200px",
                      overflowY: "auto",
                      "::-webkit-scrollbar": {
                        width: "4px",
                      },
                      "::-webkit-scrollbar-track": {
                        background: "transparent",
                      },
                      "::-webkit-scrollbar-thumb": {
                        background: "rgba(0, 0, 0, 0.1)",
                        borderRadius: "4px",
                      },
                      "& .MuiAutocomplete-option": {
                        padding: "8px 12px",
                        "&[aria-selected='true']": {
                          backgroundColor: "rgba(0, 61, 120, 0.08)",
                        },
                        "&[data-focus='true']": {
                          backgroundColor: "rgba(0, 61, 120, 0.04)",
                        },
                      },
                    },
                  },
                },
              }}
            />
          </FormControl>

          {/* Year Filter */}
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 600,
              color: "#374151",
              mb: 1,
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
            }}
          >
            <Calendar size={16} color="#8B5CF6" />
            Year
          </Typography>
          <FormControl fullWidth variant="outlined" sx={{ mb: 1.5 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Enter Year (e.g., 2023)"
              variant="outlined"
              value={selectedYear}
              error={!!yearError}
              helperText={yearError ? yearError : ""}
              onChange={(e) => handleYearChange(e)}
              sx={{
                width: "95%",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  backgroundColor: "#F8FAFC",
                  border: "1px solid #E2E8F0",
                  transition: "all 0.2s ease-in-out",
                  fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                  "&:hover": {
                    backgroundColor: "#F1F5F9",
                    borderColor: "#0073A6",
                  },
                  "&.Mui-focused": {
                    backgroundColor: "#FFFFFF",
                    boxShadow: "0 0 0 3px rgba(0, 115, 166, 0.1)",
                    borderColor: "#0073A6",
                  },
                },
                "& .MuiInputBase-input": {
                  fontSize: "14px",
                  padding: "12px 16px",
                  color: "#111827",
                },
                "& .MuiInputBase-input::placeholder": {
                  fontSize: "14px",
                  color: "#6B7280",
                },
                "& .MuiFormHelperText-root": {
                  fontSize: "12px",
                  marginLeft: "0px",
                  marginTop: "4px",
                },
              }}
              InputProps={{
                endAdornment: selectedYear && (
                  <InputAdornment position="end">
                    <IconButton 
                      size="small" 
                      onClick={clearYear}
                      sx={{
                        color: "#6B7280",
                        "&:hover": {
                          backgroundColor: "#F3F4F6",
                        },
                      }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </FormControl>

          {/* Type Filter */}
          <Typography
            sx={{
              fontSize: "0.75rem",
              fontWeight: 600,
              color: "#1F2937",
              mb: 0.5,
              ml: 0.5,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <Type size={16} color="#F59E0B" />
            Type
          </Typography>
          <FormControl fullWidth variant="outlined" sx={{ mb: 1.5 }}>
            <Autocomplete
              size="small"
              value={selectedStatuteTypeFilter || ""}
              onChange={(event, newValue) => {
                handleTypeChange(newValue);
              }}
              onInputChange={(e, newValue) => {
                if (!newValue && isFilterApplied) {
                  dispatch(
                    statuteSearchResults(
                      statuteSearchValue,
                      searchMode,
                      resultsCount,
                      (currentPage - 1) * resultsCount,
                      {
                        year: selectedYear,
                        letter: selectedLetter,
                        province: selectedStatuteProvinceFilter,
                        type: "",
                      }
                    )
                  );
                }
              }}
              disableClearable={!selectedStatuteTypeFilter}
              clearIcon={
                selectedStatuteTypeFilter ? (
                  <ClearIcon fontSize="small" />
                ) : null
              }
              options={[
                "Act",
                "Agreement",
                "Amendments",
                "Bills",
                "Code",
                "Constitution",
                "Convention",
                "Law",
                "Order",
                "Ordinance",
                "Policy",
                "Regulation",
                "Resolution",
                "Rule",
              ]}
              getOptionLabel={(option) => option || ""}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Select type"
                  sx={{
                    width: "95%",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        borderColor: "rgba(0, 61, 120, 0.2)",
                      },
                      "&.Mui-focused": {
                        boxShadow: "0 0 0 2px rgba(0, 61, 120, 0.1)",
                        borderColor: "#003D78",
                      },
                    },
                    "& .MuiInputBase-input": {
                      fontSize: "0.75rem",
                      padding: "8px 12px",
                    },
                    "& .MuiInputBase-input::placeholder": {
                      fontSize: "0.75rem",
                    },
                  }}
                />
              )}
              componentsProps={{
                paper: {
                  sx: {
                    mt: 0.5,
                    boxShadow:
                      "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                    borderRadius: "8px",
                    "& .MuiAutocomplete-listbox": {
                      fontSize: "0.75rem",
                      maxHeight: "200px",
                      overflowY: "auto",
                      "::-webkit-scrollbar": {
                        width: "4px",
                      },
                      "::-webkit-scrollbar-track": {
                        background: "transparent",
                      },
                      "::-webkit-scrollbar-thumb": {
                        background: "rgba(0, 0, 0, 0.1)",
                        borderRadius: "4px",
                      },
                      "& .MuiAutocomplete-option": {
                        padding: "8px 12px",
                        "&[aria-selected='true']": {
                          backgroundColor: "rgba(0, 61, 120, 0.08)",
                        },
                        "&[data-focus='true']": {
                          backgroundColor: "rgba(0, 61, 120, 0.04)",
                        },
                      },
                    },
                  },
                },
              }}
            />
          </FormControl>

          {/* Province Filter */}
          <Typography
            sx={{
              fontSize: "0.75rem",
              fontWeight: 600,
              color: "#1F2937",
              mb: 0.5,
              ml: 0.5,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <MapPin size={16} color="#10B981" />
            Province
          </Typography>
          <FormControl fullWidth variant="outlined" sx={{ mb: 1.5 }}>
            <Autocomplete
              size="small"
              value={selectedStatuteProvinceFilter || ""}
              onChange={(event, newValue) => {
                handleProvinceChange(newValue);
              }}
              onInputChange={(e, newValue) => {
                if (!newValue && isFilterApplied) {
                  dispatch(
                    statuteSearchResults(
                      statuteSearchValue,
                      searchMode,
                      resultsCount,
                      (currentPage - 1) * resultsCount,
                      {
                        year: selectedYear,
                        letter: selectedLetter,
                        province: "",
                        type: selectedStatuteTypeFilter,
                      }
                    )
                  );
                }
              }}
              disableClearable={!selectedStatuteProvinceFilter}
              clearIcon={
                selectedStatuteProvinceFilter ? (
                  <ClearIcon fontSize="small" />
                ) : null
              }
              options={[
                "Federal",
                "Punjab",
                "Sindh",
                "Balochistan",
                "Khyber Pakhtunkhwa",
                "Gilgit Baltistan",
                "Azad Kashmir And Jammu",
                "Other",
              ]}
              getOptionLabel={(option) => option || ""}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Select Province"
                  sx={{
                    width: "95%",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        borderColor: "rgba(0, 61, 120, 0.2)",
                      },
                      "&.Mui-focused": {
                        boxShadow: "0 0 0 2px rgba(0, 61, 120, 0.1)",
                        borderColor: "#003D78",
                      },
                    },
                    "& .MuiInputBase-input": {
                      fontSize: "0.75rem",
                      padding: "8px 12px",
                    },
                    "& .MuiInputBase-input::placeholder": {
                      fontSize: "0.75rem",
                    },
                  }}
                />
              )}
              componentsProps={{
                paper: {
                  sx: {
                    mt: 0.5,
                    boxShadow:
                      "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                    borderRadius: "8px",
                    "& .MuiAutocomplete-listbox": {
                      fontSize: "0.75rem",
                      maxHeight: "200px",
                      overflowY: "auto",
                      "::-webkit-scrollbar": {
                        width: "4px",
                      },
                      "::-webkit-scrollbar-track": {
                        background: "transparent",
                      },
                      "::-webkit-scrollbar-thumb": {
                        background: "rgba(0, 0, 0, 0.1)",
                        borderRadius: "4px",
                      },
                      "& .MuiAutocomplete-option": {
                        padding: "8px 12px",
                        "&[aria-selected='true']": {
                          backgroundColor: "rgba(0, 61, 120, 0.08)",
                        },
                        "&[data-focus='true']": {
                          backgroundColor: "rgba(0, 61, 120, 0.04)",
                        },
                      },
                    },
                  },
                },
              }}
            />
          </FormControl>
        </Box>

        {/* Footer Actions */}
        <Box
          sx={{
            padding: "12px 16px 16px 16px",
            borderTop: "1px solid #F1F5F9",
            backgroundColor: "#FFFFFF",
            borderRadius: "0 0 8px 8px",
            marginTop: "auto",
          }}
        >
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<X size={16} />}
              onClick={handleClearFilters}
              sx={{
                flex: 1,
                textTransform: "none",
                fontSize: "12px",
                fontWeight: 500,
                color: "#EF4444",
                borderColor: "#FECACA",
                backgroundColor: "#FEF2F2",
                borderRadius: "8px",
                padding: "8px 16px",
                height: "36px",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  backgroundColor: "#FEE2E2",
                  borderColor: "#FCA5A5",
                },
              }}
            >
              Clear
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<Check size={16} />}
              disabled={
                !selectedLetter &&
                !selectedYear &&
                !selectedStatuteTypeFilter &&
                !selectedStatuteProvinceFilter
              }
              onClick={toggleSidebar}
              sx={{
                flex: 1,
                textTransform: "none",
                fontSize: "12px",
                fontWeight: 500,
                backgroundColor: tabColors.primary,
                borderRadius: "8px",
                padding: "8px 16px",
                height: "36px",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  backgroundColor: tabColors.hover,
                  boxShadow: `0 2px 8px ${tabColors.primary}30`,
                },
                "&.Mui-disabled": {
                  backgroundColor: "#F3F4F6",
                  color: "#9CA3AF",
                },
              }}
            >
              Apply
            </Button>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default StatuteSearchSidebar;
